// The small, always-loaded part of the arcade: cabinet size, the "which
// game is being placed" arm, and the lazy loader for the games registry.
// Everything heavier (the cabinet, popup, VR panels, the games themselves)
// lives in chunks that load only when a world has a cabinet or the builder
// opens the Games menu.

import { useEffect, useState } from 'react';
import type { ArcadeGameDef } from './types';
import type { LeaderboardConfig, PrimitiveItem } from '../../../stores/worldStore';

/** Cabinet footprint and height (metres). The primitive's origin is the centre of this box. */
export const ARC_W = 0.74;
export const ARC_D = 0.8;
export const ARC_H = 1.86;
/** Primitive y that stands the cabinet on a floor at y = 0. */
export const ARC_REST_Y = ARC_H / 2;

// The games registry (arcade/registry.ts) is code-split: it loads the first
// time a world with a cabinet, or the builder's Games menu, needs it.
let cache: ArcadeGameDef[] | null = null;
let pending: Promise<ArcadeGameDef[]> | null = null;

export function loadArcadeGames(): Promise<ArcadeGameDef[]> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = import('./registry').then((m) => m.ARCADE_GAMES ?? [])
      .then((list) => { cache = list; return list; })
      .catch((e) => { console.warn('[arcade] could not load the games', e); pending = null; return [] as ArcadeGameDef[]; });
  }
  return pending;
}

/** Already-loaded game by id (null until loadArcadeGames() has resolved). */
export function arcadeGameSync(id: string | null | undefined): ArcadeGameDef | null {
  return (id && cache?.find((g) => g.id === id)) || null;
}

/** All arcade games (loads the registry on first use). */
export function useArcadeGames(enabled = true): { games: ArcadeGameDef[]; ready: boolean } {
  const [games, setGames] = useState<ArcadeGameDef[] | null>(cache);
  useEffect(() => {
    if (!enabled || games) return;
    let live = true;
    void loadArcadeGames().then((g) => { if (live) setGames(g); });
    return () => { live = false; };
  }, [enabled, games]);
  return { games: games ?? [], ready: !!games };
}

/** One arcade game by id; undefined while loading, null if this build doesn't have it. */
export function useArcadeGame(id: string | null | undefined): ArcadeGameDef | null | undefined {
  const { games, ready } = useArcadeGames(!!id);
  if (!ready) return undefined;
  return games.find((g) => g.id === id) ?? null;
}

// The builder arms "place an arcade cabinet" with a game; the VR placement
// tools (which only know the shape) read it back when the cabinet is dropped.
let armed: string | null = null;
export function armArcadePlacement(id: string) { armed = id; }
export function armedArcade(): string | null { return armed; }

/** Cabinet name in the scene list. */
export const arcadeCabinetName = (id: string | null | undefined) => `${arcadeGameSync(id)?.title ?? 'Arcade'} Cabinet`;

/** The art as a data URL (menu thumbnails, <img>). */
export const artDataUrl = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

// ---- arcade leaderboard boards --------------------------------------------------

export const DEFAULT_BOARD: LeaderboardConfig = {
  game: 'all', period: 'all', scope: 'world', title: 'HIGH SCORES', theme: 'auto', rows: 10, mount: 'stand',
};
export const boardConfig = (p: Pick<PrimitiveItem, 'board'> | null | undefined): LeaderboardConfig => ({ ...DEFAULT_BOARD, ...(p?.board ?? {}) });

/** Palettes match the cabinets; 'auto' follows the game shown. */
export const BOARD_THEMES: { id: string; label: string; c1: string; c2: string }[] = [
  { id: 'auto', label: 'Game colours', c1: '#00c2b3', c2: '#ffd166' },
  { id: 'gathrxr', label: 'GathrXR', c1: '#00c2b3', c2: '#ffd166' },
  { id: 'neon', label: 'Neon pink', c1: '#ff4fa3', c2: '#ffc83d' },
  { id: 'lagoon', label: 'Lagoon', c1: '#22d3c5', c2: '#c026d3' },
  { id: 'sky', label: 'Sky', c1: '#38bdf8', c2: '#f472b6' },
  { id: 'acid', label: 'Acid', c1: '#a3e635', c2: '#f97316' },
];

/** Board face size (m) at scale 1. */
export const LB_W = 1.6;
export const lbFaceH = (rows: number) => (rows === 5 ? 1.0 : 1.5);
/** Freestanding boards stand on two legs; the face's bottom edge is this high. */
export const LB_LEGS = 0.9;

/** The pick box (and placement ghost) for a board: [w, h, d]; the primitive's origin is its centre. */
export function boardBox(cfg: LeaderboardConfig): [number, number, number] {
  const h = lbFaceH(cfg.rows);
  return cfg.mount === 'stand' ? [LB_W + 0.1, h + LB_LEGS, 0.6] : [LB_W + 0.08, h + 0.08, 0.08];
}
/** Primitive y that puts it on a floor at y = 0 (stand) or hangs its centre at eye height (wall). */
export function boardRestY(cfg: LeaderboardConfig): number {
  return cfg.mount === 'stand' ? boardBox(cfg)[1] / 2 : 1.75;
}
/**
 * New primitive y after a config / size change: freestanding boards keep
 * their feet on the floor, wall boards keep their face centre where it was.
 */
export function reframeBoardY(y: number, scaleY: number, from: LeaderboardConfig, to: LeaderboardConfig, toScaleY = scaleY): number {
  const faceCentre = (c: LeaderboardConfig, sy: number, originY: number) =>
    c.mount === 'stand' ? originY - (boardBox(c)[1] / 2) * sy + (LB_LEGS + lbFaceH(c.rows) / 2) * sy : originY;
  const floor = from.mount === 'stand' ? y - (boardBox(from)[1] / 2) * scaleY : null;
  if (to.mount === 'stand') {
    // Off the wall: stand it on the floor below (a ground-level floor when it's within half a metre).
    let f = floor ?? faceCentre(from, scaleY, y) - (LB_LEGS + lbFaceH(to.rows) / 2) * toScaleY;
    if (floor === null && Math.abs(f) < 0.6) f = 0;
    return +(f + (boardBox(to)[1] / 2) * toScaleY).toFixed(3);
  }
  return +faceCentre(from, scaleY, y).toFixed(3);
}

let armedBoard: LeaderboardConfig = { ...DEFAULT_BOARD };
export function armBoardPlacement(cfg?: Partial<LeaderboardConfig>) { armedBoard = { ...DEFAULT_BOARD, ...(cfg ?? {}) }; }
export function armedBoardConfig(): LeaderboardConfig { return armedBoard; }
